/**
 * The app's composition root — EXTRACTED from `main.tsx` (#236 follow-up,
 * harness/composition convergence) so tests can mount the ACTUAL shipped
 * wiring (the composition smoke family mounts `<Composition />` under
 * `StrictMode`; `main.tsx` keeps only the boot seam). The wiring that runs
 * is the wiring tested: the port constructions live in importable factory
 * hooks (`providers/useMessageComposePort`,
 * `useSessionProvider`, …) shared with the test harness.
 *
 * Layering (mounted by `main.tsx`):
 *
 *   StrictMode
 *     ↳ AppErrorBoundary           // outermost render-time safety net
 *         ↳ AppProviders           // QueryClientProvider
 *             ↳ Composition        // builds Ports + mounts router + shell
 *                 ↳ PortsProvider  // per-port factory hooks → PortsContext
 *                     ↳ BrowserRouter  // react-router context
 *                         ↳ OnboardingGate
 *                             ↳ ContactVerificationGate
 *                                 ↳ RouteRegistry  // single sanctioned <Routes>
 *
 * ## Composition decisions (carried verbatim from the entry point)
 *
 *   1. `QueryClientProvider` is preserved at the root; the root-level client
 *      is cheap and keeps the tree compatible with any adapter that wants to
 *      share React Query's cache.
 *   2. `RouteRegistry` owns the only `<Routes>` / `<Route>` JSX in
 *      production source (FR-004, T044 architecture check). This file
 *      contains zero of either element.
 *   3. Shell chrome mounts per-descriptor inside `RouteRegistry`'s element
 *      projection — NOT at the composition root (021-cms slice A1).
 *   4. Access control lives in `RouteGuard` via the `SessionPort` seam
 *      (FR-013), wired by `RouteRegistry` per descriptor flag.
 *   5. `AppErrorBoundary` (mounted by `main.tsx`) backstops the per-route
 *      `PortErrorBoundary` instances; see its JSDoc.
 *
 * ## Ports composition
 *
 * `ports.session` is the single BFF-backed `RealSessionProvider` (spec 023 +
 * the 032 dual-audience collapse, RD-111 / T015): it reports `admin`, `member`,
 * or `anonymous` from one `session.me` round-trip. See
 * `session/useSessionProvider.ts` for construction, the dispose lifecycle, and
 * the StrictMode revive. The `sessionDeps` prop is the injection seam that hook
 * exposes (`createProvider` / `trpc`), forwarded so the composition smoke family
 * can mount THIS component with a network-stubbed session provider; production
 * passes nothing. The demo-controls slot was REMOVED from `Ports` in the D-060
 * client rescission (T035) — the real provider carries no demo controls and the
 * SPA `/login` demo form is gone, so there is nothing to feed.
 */

import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import type { JSX, ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { BrowserRouter } from 'react-router';
import type { AnalyticsCapturePort } from './analytics/AnalyticsCapturePort.js';
import { AnalyticsCaptureProvider } from './analytics/AnalyticsCaptureProvider.js';
import type { AnalyticsConsentPort } from './analytics/AnalyticsConsentPort.js';
import { AnalyticsConsentProvider } from './analytics/AnalyticsConsentProvider.js';
import type { AnalyticsReadPort } from './analytics/AnalyticsReadPort.js';
import { AnalyticsReadProvider } from './analytics/AnalyticsReadProvider.js';
import { TrpcAnalyticsCaptureAdapter } from './analytics/TrpcAnalyticsCaptureAdapter.js';
import { TrpcAnalyticsConsentAdapter } from './analytics/TrpcAnalyticsConsentAdapter.js';
import { TrpcAnalyticsReadAdapter } from './analytics/TrpcAnalyticsReadAdapter.js';
import { AdminTrpcProvider, useAdminTrpcClient } from './components/cms/hooks/useAdminContent.js';
import { ContactVerificationGate } from './contact-verification/ContactVerificationGate.js';
import { CategoryNotFoundError } from './content/CategoryNotFoundError.js';
import type { LanguageCode } from './i18n/LanguagePort.js';
import { OnboardingGate } from './onboarding/OnboardingGate.js';
import { PortsProvider } from './providers/PortsProvider.js';
import { useAccountDeletionPort } from './providers/useAccountDeletionPort.js';
import { useContactVerificationPort } from './providers/useContactVerificationPort.js';
import { useContentPort } from './providers/useContentPort.js';
import { useFeedbackSubmitPort } from './providers/useFeedbackSubmitPort.js';
import { useLanguagePort } from './providers/useLanguagePort.js';
import { useMemberCasesPort } from './providers/useMemberCasesPort.js';
import { useMemberProfilePort } from './providers/useMemberProfilePort.js';
import { useMembershipBillingPort } from './providers/useMembershipBillingPort.js';
import { useMembershipCertificatePort } from './providers/useMembershipCertificatePort.js';
import { useMembershipResignationPort } from './providers/useMembershipResignationPort.js';
import { useMessageComposePort } from './providers/useMessageComposePort.js';
import { useMyDataExportPort } from './providers/useMyDataExportPort.js';
import { useMyDataPort } from './providers/useMyDataPort.js';
import { usePostalCodeLookupPort } from './providers/usePostalCodeLookupPort.js';
import { SessionReplacedNotice, SessionStatusBoundary } from './session/SessionStatusBoundary.js';
import { reloadPage, useSessionCacheCleanup } from './session/useSessionCacheCleanup.js';
import { type UseSessionProviderDeps, useSessionProvider } from './session/useSessionProvider.js';
import { RouteRegistry } from './shell/RouteRegistry.js';
import { createWebTrpcClient } from './trpc/client.js';

export interface CompositionProps {
  /**
   * Session-provider injection seam (the composition smoke family passes a
   * stubbed provider via `useSessionProvider`'s own `createProvider` / `trpc`
   * deps). Production (`main.tsx`) passes nothing — the default single
   * `RealSessionProvider` is constructed (RD-111).
   */
  readonly sessionDeps?: UseSessionProviderDeps;
  /** Test-only capture transport seam; production uses the typed cookie client. */
  readonly analyticsCapturePort?: AnalyticsCapturePort;
  /** Test-only member-consent transport seam; production uses the typed cookie client. */
  readonly analyticsConsentPort?: AnalyticsConsentPort;
  /** Test-only analytics read seam; production uses the typed cookie client. */
  readonly analyticsReadPort?: AnalyticsReadPort;
}

export function Composition({
  sessionDeps,
  analyticsCapturePort,
  analyticsConsentPort,
  analyticsReadPort,
}: CompositionProps = {}): JSX.Element {
  // `ports.session` is the single BFF `RealSessionProvider` (spec 023 + the 032
  // collapse, RD-111 / T015); construction + dispose + the StrictMode revive are
  // owned by `useSessionProvider` (see its JSDoc and `main.tsx`'s header history).
  const sessionProvider = useSessionProvider(sessionDeps);

  // One cookie-authenticated passive capture adapter and one browser owner;
  // neither identity nor route URLs are supplied by this composition seam.
  const [defaultAnalyticsCapture] = useState(
    () => new TrpcAnalyticsCaptureAdapter(createWebTrpcClient()),
  );
  const analyticsCapture = analyticsCapturePort ?? defaultAnalyticsCapture;
  const [defaultAnalyticsConsent] = useState(
    () => new TrpcAnalyticsConsentAdapter(createWebTrpcClient()),
  );
  const analyticsConsent = analyticsConsentPort ?? defaultAnalyticsConsent;
  const [defaultAnalyticsRead] = useState(
    () => new TrpcAnalyticsReadAdapter(createWebTrpcClient()),
  );
  const analyticsRead = analyticsReadPort ?? defaultAnalyticsRead;

  // The member's language is a server-stored setting (059): `session.me`
  // carries it in, `member.language.set` stores a member's switch.
  const [saveLanguage] = useState(() => {
    const client = createWebTrpcClient();
    return async (next: LanguageCode): Promise<void> => {
      const setLanguage = client.member?.language?.set;
      if (setLanguage === undefined) {
        throw new Error('member.language.set is not mounted');
      }
      await setLanguage.mutate({ language: next });
    };
  });
  const language = useLanguagePort({ session: sessionProvider, save: saveLanguage });
  // `content` is filled by `TrpcContentReader` (content-and-knowledge Phase A
  // / TA15). The test harness (`renderAppWithPorts`) deliberately does NOT
  // instantiate this factory — per-test composition keeps the slot omitted
  // by default and the missing-port stub semantics survive (documented
  // omission, reaffirmed at the #236 convergence round).
  const content = useContentPort();
  // Message-compose port (027) + the 030 demo-coherence seam, INCLUDING the
  // #236 stale-closure guard — the hook tracks the CURRENT session through a
  // render-updated ref, surviving the StrictMode composite revive. Shared
  // factory hook, imported by the harness too.
  const messageCompose = useMessageComposePort();
  // Admin authoring tRPC client (cookie-authenticated post-D-106; independent
  // of the `content` slot per spec 021-cms research RD-007); travels via its
  // own React Context so consumer hooks resolve it without widening `Ports`.
  const adminTrpcClient = useAdminTrpcClient();
  const memberProfile = useMemberProfilePort();
  const sessionCache = useSessionCacheCleanup(sessionProvider, memberProfile, useQueryClient());
  const membershipBilling = useMembershipBillingPort();
  const membershipCertificate = useMembershipCertificatePort();
  // Member resignation (056) — the REAL tRPC adapter over member.resignation.submit.
  const membershipResignation = useMembershipResignationPort();
  // Consolidated GDPR-access read (033 US1) — the REAL tRPC-backed reader over
  // `member.myData.view` (first real consumer of the 033 Foundational layer).
  const myData = useMyDataPort();
  // Member cases-list read (034 US1) — the REAL tRPC reader over member.cases.list.
  const memberCases = useMemberCasesPort();
  // Member account erasure (033 US2) — the REAL tRPC-backed adapter over
  // `member.myData.requestDeletion`, backing the DeleteAccountConfirm flow.
  const accountDeletion = useAccountDeletionPort();
  // Member data portability (033 US5) — the REAL tRPC-backed adapter over
  // `member.myData.export`, backing the Tallennetut tiedot export affordance.
  const myDataExport = useMyDataExportPort();
  // Member feedback submit (033 US5) — the REAL tRPC-backed adapter over
  // `member.feedback.submit`, retiring the FeedbackModal stub (FR-057).
  const feedbackSubmit = useFeedbackSubmitPort();
  // Contact-verification cadence port (026 US4).
  const contactVerification = useContactVerificationPort();
  // Postal-code → city lookup port (026 US4).
  const postalCodeLookup = usePostalCodeLookupPort();
  // react-compiler-exception: PortsContext.Provider value — stable identity is the consumer-effect contract
  const ports = useMemo(
    () => ({
      session: sessionProvider,
      language,
      content,
      messageCompose,
      memberProfile,
      membershipBilling,
      membershipCertificate,
      membershipResignation,
      myData,
      memberCases,
      accountDeletion,
      myDataExport,
      feedbackSubmit,
      contactVerification,
      postalCodeLookup,
    }),
    [
      sessionProvider,
      language,
      content,
      messageCompose,
      memberProfile,
      membershipBilling,
      membershipCertificate,
      membershipResignation,
      myData,
      memberCases,
      accountDeletion,
      myDataExport,
      feedbackSubmit,
      contactVerification,
      postalCodeLookup,
    ],
  );

  // Port-error-boundary wiring (TA22 closure): per-route via the
  // `portBoundary` descriptor flag in `shell/routes.ts` (see that file +
  // `RouteRegistry.tsx` for the dispatch logic). OnboardingGate sits between
  // the router and `RouteRegistry` so the wizard mounts before any protected
  // route renders (016, spec §Integration Point); shell chrome mounts
  // per-descriptor inside `RouteRegistry`, not here.
  return (
    <PortsProvider ports={ports}>
      <AdminTrpcProvider client={adminTrpcClient}>
        <BrowserRouter>
          <AnalyticsReadProvider port={analyticsRead}>
            <AnalyticsConsentProvider port={analyticsConsent}>
              <AnalyticsCaptureProvider port={analyticsCapture}>
                {sessionCache.replaced ? (
                  <SessionReplacedNotice reload={reloadPage} />
                ) : (
                  <SessionStatusBoundary key={sessionCache.generation}>
                    <OnboardingGate>
                      <ContactVerificationGate>
                        <RouteRegistry />
                      </ContactVerificationGate>
                    </OnboardingGate>
                  </SessionStatusBoundary>
                )}
              </AnalyticsCaptureProvider>
            </AnalyticsConsentProvider>
          </AnalyticsReadProvider>
        </BrowserRouter>
      </AdminTrpcProvider>
    </PortsProvider>
  );
}

export function AppProviders({ children }: { readonly children: ReactNode }): JSX.Element {
  // `useState` memoises the QueryClient for the component's lifetime —
  // recreating it on every render would reset React Query's cache and
  // drop every in-flight request. The lazy initialiser runs exactly
  // once per mount; the setter is intentionally discarded because the
  // client is not React state.
  //
  // `retry` predicate: `CategoryNotFoundError` is a deterministic miss
  // (the slug does not exist; retrying will produce the same NOT_FOUND
  // every time), so the default `retry: 3` would fire two extra wasted
  // tRPC round-trips before the error reaches the UI. Every other error
  // class keeps the React Query default behaviour (transient failures
  // → up to 3 attempts).
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: (count, error) => (error instanceof CategoryNotFoundError ? false : count < 3),
          },
        },
      }),
  );

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}
